import { useEffect, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { identify, onAnalyticsReady, track } from '@fff-web/fff-analytics';
import { Button, LoadingSpinner } from '@fff-web/fff-ui-shared';
import { toExternalId, useUserContext } from '@fff-web/fff-utilities';
import { BoxImage, HeroImage } from '..';
import invalidateCaches from '../../../../helpers/invalidateCaches';
import {
  getPriceLock,
  postPriceLock,
  reactivateCancelledSubscription,
} from '../../../../services/subscription';
import * as routePaths from '../../../../utils/routePaths';
import { trackReactivation } from '../../../../utils/tracking/subscription';
import { PriceLockStatusMessage } from '../../../constants';
import { LockInRateStatus } from '../../LockInRate.types';
import {
  getCurrentMembershipPrice,
  legalNote,
  PRICE_INCREASE,
  ratesDisclaimer,
  type SubscriptionType_State,
} from './LockInRateView.utils';
import * as styles from './LockInRateView.module.scss';

export interface LockInRateProps {
  initialLockedRateStatus: LockInRateStatus | null;
}

export const LockInRateView = ({ initialLockedRateStatus }: LockInRateProps) => {
  // State
  const getPriceLockQuery = useQuery({
    queryKey: ['getPriceLock'],
    queryFn: () => getPriceLock(initialLockedRateStatus),
  });
  const postPriceLockMutation = useMutation({
    mutationFn: postPriceLock,
  });
  const reactivateCancelledSubscriptionMutation = useMutation({
    mutationFn: reactivateCancelledSubscription,
  });
  const [lockedRateStatus, setLockedRateStatus] = useState<LockInRateStatus | null>(
    initialLockedRateStatus,
  );
  const [lockedRateStatusMessage, setLockedRateStatusMessage] = useState('');
  const {
    userInfo: { user },
  } = useUserContext();

  // Constants
  const { subscriptionState, subscriptionType, planCode } = user;
  const isLockedRateStatus: boolean =
    lockedRateStatus === 'PAYMENT_PENDING' ||
    lockedRateStatus === 'PAYMENT_PROCESSED' ||
    lockedRateStatus === 'PAYMENT_FAILURE';
  const isUserCanceled = subscriptionState.toUpperCase() === 'CANCELED';
  const isUserExpired = subscriptionState.toUpperCase() === 'EXPIRED';
  const showPurchaseButton = !isUserExpired && !isLockedRateStatus;
  const copyKey =
    `${subscriptionType.toUpperCase()}_${subscriptionState.toUpperCase()}` as SubscriptionType_State;

  // Effects
  useEffect(() => {
    if (!getPriceLockQuery.data) return;

    setLockedRateStatus(getPriceLockQuery.data.status as LockInRateStatus);
    setLockedRateStatusMessage(
      PriceLockStatusMessage[getPriceLockQuery.data.status as LockInRateStatus],
    );
  }, [getPriceLockQuery.data]);

  // Handlers
  const onServiceError = () => {
    setLockedRateStatus('PAYMENT_FAILURE');
    setLockedRateStatusMessage(
      'Oops something went wrong! Please contact customer service support directly to lock in your price.',
    );
  };

  const handlePurchase = () => {
    postPriceLockMutation.mutate(undefined, {
      onSuccess: (response) => {
        setLockedRateStatus('PAYMENT_PENDING');
        setLockedRateStatusMessage(PriceLockStatusMessage.PAYMENT_PENDING);

        const event = { trackName: 'Lock In Rate Requested' };
        const isStatusSuccess = response?.status === 200;
        const isStatusFailure = !isStatusSuccess && response?.status !== 204;

        if (isStatusSuccess) {
          const purchaseAmount: number = getCurrentMembershipPrice(subscriptionType);
          const externalId = toExternalId(user?.uuid);

          onAnalyticsReady(() => {
            track({
              bypassConsentCheck: true,
              ...event,
              properties: {
                status: 'success',
                plan_code: planCode,
                amount: purchaseAmount,
                external_id: externalId,
              },
            });
          });

          const email = user?.email;
          if (window.fffPactSafe) {
            window.fffPactSafe.signContractsForGroup(
              email,
              process.env.REACT_APP_PACT_SAFE_LOCK_RATE_GROUP_KEY,
            );
          }
        } else if (isStatusFailure) {
          track({ ...event, properties: { status: 'failure' } });
          setLockedRateStatus('PAYMENT_FAILURE');
          setLockedRateStatusMessage(PriceLockStatusMessage.PAYMENT_FAILURE);
        }
      },
      onError: onServiceError,
    });
  };

  const handleReactivate = () => {
    reactivateCancelledSubscriptionMutation.mutate(undefined, {
      onSuccess: async (response) => {
        // 1) Track reactivation event
        trackReactivation({ response, user });

        // 2) Then call handlePurchase
        if (response?.ok) {
          invalidateCaches();
          identify();
          handlePurchase();
        } else {
          onServiceError();
        }
      },
      onError: onServiceError,
    });
  };

  // Views
  if (getPriceLockQuery.isLoading || lockedRateStatus === null) return <LoadingSpinner />;

  return (
    <div className={styles.lirContainer} data-testid="lock-in-rate-container">
      <div className={styles.lirHeroWrapper} data-testid="lock-in-rate-hero-wrapper">
        <HeroImage className={styles.lirHero} data-testid="lock-in-rate-hero" />
      </div>

      <div className={styles.lirContentWrapper} data-testid="lock-in-rate-content-wrapper">
        <div className={styles.lirBoxContainer} data-testid="lock-in-rate-box-container">
          <BoxImage className={styles.lirBoxImage} data-testid="lock-in-rate-box-image" />
        </div>
        <p className={styles.lirOffer}>LIMITED TIME OFFER</p>
        <h1 className={styles.lirTitle}>Lock In Your Price</h1>

        <p className={styles.lirDescription} data-testid="lock-in-rate-description">
          {ratesDisclaimer[copyKey]} <br />
          <b>
            Hurry, this offer ends {PRICE_INCREASE.increaseDate} at {PRICE_INCREASE.increaseHour}.
          </b>
        </p>

        <p className={styles.lirLegalNote} data-testid="lock-in-rate-legal-note">
          {legalNote[copyKey]} Taxes, shipping and handling fees, and, for non-US customers, foreign
          exchange or transaction fees may apply. You may cancel your subscription at any time
          before your next bill date online or by contacting FabFitFun Support{' '}
          <a href={process.env.REACT_APP_SUPPORT_URL} className={styles.lirLegalNoteLinks}>
            here
          </a>
          . For more details, click{' '}
          <a
            href={`${process.env.REACT_APP_SUPPORT_URL}/articles/47-how-do-i-cancel-my-membership`}
            className={styles.lirLegalNoteLinks}
          >
            here
          </a>
          .
          <br />
          <br />
          You are also agreeing to our{' '}
          <a
            href={`${process.env.REACT_APP_LEGAL_URL}/#terms-of-use`}
            className={styles.lirLegalNoteLinks}
          >
            Terms of Use and Sale
          </a>{' '}
          and{' '}
          <a
            href={`${process.env.REACT_APP_LEGAL_URL}/#box-membership-terms`}
            className={styles.lirLegalNoteLinks}
          >
            FabFitFun Membership Terms
          </a>
          .
        </p>

        {isUserExpired && (
          <p
            className={styles.alreadyLockedRate}
            id="non-active-message"
            data-testid="non-active-message"
          >
            Oops! Your membership is {subscriptionState.toLowerCase()}. Please{' '}
            <a href={routePaths.REACTIVATE_MEMBERSHIP} className={styles.legalNoteLinks}>
              reactivate your membership
            </a>{' '}
            to lock-in the ${PRICE_INCREASE.annual.membership.current} yearly rate.
          </p>
        )}
        {isLockedRateStatus && (
          <p className={styles.alreadyLockedRate} id="locked-message" data-testid="locked-message">
            {lockedRateStatusMessage}
          </p>
        )}
        {showPurchaseButton && (
          <div className={styles.lockButtonContainer}>
            <Button
              containerFullWidth
              onClick={isUserCanceled ? handleReactivate : handlePurchase}
              loading={
                postPriceLockMutation.isPending || reactivateCancelledSubscriptionMutation.isPending
              }
              disabled={
                postPriceLockMutation.isPending ||
                postPriceLockMutation.isSuccess ||
                reactivateCancelledSubscriptionMutation.isPending
              }
            >
              PURCHASE NOW
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};
